/**
 * useTaskSync - Tasks.md ↔ 칸반보드 동기화 훅
 *
 * 기능:
 * 1. Tasks 문서 (category="tasks") 조회/생성
 * 2. 칸반보드 변경 → Tasks.md 자동 업데이트
 * 3. Tasks.md 파싱 → 칸반보드 데이터 생성
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { specDocumentService, type SpecDocument } from '@/services/specDocumentService';
import {
  parseTasksMd,
  boardToTasksMd,
  generateDefaultTasksMd,
} from '@/lib/taskMdParser';
import type { Task, TaskBoardData } from '@/types';

interface UseTaskSyncOptions {
  projectId: string;
  autoSync?: boolean; // 자동 동기화 활성화 (기본: true)
  syncInterval?: number; // 동기화 간격 (ms, 기본: 5000)
}

interface UseTaskSyncReturn {
  // 상태
  tasksDocument: SpecDocument | null;
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncedAt: Date | null;
  error: string | null;

  // 액션
  syncToDocument: (board: TaskBoardData) => Promise<void>;
  syncFromDocument: () => Promise<TaskBoardData | null>;
  createTasksDocument: () => Promise<SpecDocument | null>;
  refreshDocument: () => Promise<void>;
}

export function useTaskSync({
  projectId,
  autoSync = true,
  syncInterval = 5000,
}: UseTaskSyncOptions): UseTaskSyncReturn {
  const [tasksDocument, setTasksDocument] = useState<SpecDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSyncRef = useRef<TaskBoardData | null>(null);

  // Tasks 문서 조회
  const loadTasksDocument = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await specDocumentService.getDocuments(projectId, {
        category: 'tasks',
      });

      // response가 undefined이거나 data가 없는 경우 처리
      const documents = response?.data ?? [];

      if (documents.length > 0) {
        // Get full document with content
        const fullDoc = await specDocumentService.getDocument(
          projectId,
          documents[0].id
        );
        setTasksDocument(fullDoc);
      } else {
        setTasksDocument(null);
      }
    } catch (err) {
      console.error('Failed to load tasks document:', err);
      setError('Tasks 문서를 불러오는데 실패했습니다.');
      setTasksDocument(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Tasks 문서 생성
  // 참고: tasks 문서는 다른 필수 문서(PRD, TRD 등)가 먼저 생성되어야 합니다.
  // 의존성이 충족되지 않으면 조용히 실패하고 null을 반환합니다.
  const createTasksDocument = useCallback(async (): Promise<SpecDocument | null> => {
    try {
      setIsSyncing(true);
      setError(null);

      // 템플릿에서 생성 시도
      try {
        const newDoc = await specDocumentService.createFromTemplate(projectId, 'tasks');
        setTasksDocument(newDoc);
        return newDoc;
      } catch (templateError) {
        // 템플릿 생성 실패 - dependency 문제일 수 있음
        // 422 오류(의존성 미충족)인 경우 조용히 처리
        const isDepError = (templateError as { response?: { status?: number } })?.response?.status === 422;
        if (isDepError) {
          console.debug('Tasks document cannot be created yet - dependencies not satisfied');
          return null;
        }

        // 다른 오류인 경우 직접 생성 시도
        try {
          const newDoc = await specDocumentService.createDocument(projectId, {
            title: 'Tasks',
            category: 'tasks',
            content: generateDefaultTasksMd(),
          });
          setTasksDocument(newDoc);
          return newDoc;
        } catch (createError) {
          // 직접 생성도 실패 - dependency 문제일 가능성 높음
          const isCreateDepError = (createError as { response?: { status?: number } })?.response?.status === 422;
          if (isCreateDepError) {
            console.debug('Tasks document cannot be created yet - dependencies not satisfied');
            return null;
          }
          throw createError;
        }
      }
    } catch (err) {
      console.error('Failed to create tasks document:', err);
      setError('Tasks 문서 생성에 실패했습니다.');
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [projectId]);

  // 칸반보드 → Tasks.md 동기화 (debounced)
  const syncToDocument = useCallback(
    async (board: TaskBoardData) => {
      // 문서가 없으면 먼저 생성
      let doc = tasksDocument;
      if (!doc) {
        doc = await createTasksDocument();
        if (!doc) return;
      }

      // Debounce: 이전 타이머 취소
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      pendingSyncRef.current = board;

      // 일정 시간 후 동기화
      syncTimeoutRef.current = setTimeout(async () => {
        const boardToSync = pendingSyncRef.current;
        if (!boardToSync || !doc) return;

        try {
          setIsSyncing(true);

          const newContent = boardToTasksMd(boardToSync);
          await specDocumentService.updateDocument(projectId, doc.id, {
            content: newContent,
          });

          setLastSyncedAt(new Date());
          pendingSyncRef.current = null;
        } catch (err) {
          console.error('Failed to sync to document:', err);
          setError('Tasks 문서 동기화에 실패했습니다.');
        } finally {
          setIsSyncing(false);
        }
      }, 1000); // 1초 debounce
    },
    [projectId, tasksDocument, createTasksDocument]
  );

  // Tasks.md → 칸반보드 동기화
  const syncFromDocument = useCallback(async (): Promise<TaskBoardData | null> => {
    if (!tasksDocument?.content) {
      return null;
    }

    try {
      const parsedTasks = parseTasksMd(tasksDocument.content);

      // 파싱된 태스크를 DB 태스크와 동기화
      // 이 부분은 백엔드와의 협업이 필요하므로 현재는
      // 파싱된 데이터를 바로 보드 데이터로 변환

      const board: TaskBoardData = {
        todo: [],
        in_progress: [],
        in_review: [],
        done: [],
      };

      parsedTasks.forEach((task, index) => {
        const fullTask: Task = {
          id: `parsed-${index}`,
          projectId,
          title: task.title,
          description: task.description || '',
          status: task.status,
          priority: task.priority,
          assigneeName: task.assignee,
          dueDate: task.dueDate,
          labels: task.labels,
          position: index,
          metadata: {},
          isOverdue: false,
          isDueSoon: false,
          canComplete: task.status !== 'done',
          canReopen: task.status === 'done',
          hasSourceArtifact: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        if (task.dueDate) {
          const dueDate = new Date(task.dueDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          fullTask.isOverdue = dueDate < today && task.status !== 'done';
        }

        if (task.status !== 'cancelled') {
          board[task.status].push(fullTask);
        }
      });

      return board;
    } catch (err) {
      console.error('Failed to parse tasks document:', err);
      setError('Tasks 문서 파싱에 실패했습니다.');
      return null;
    }
  }, [tasksDocument, projectId]);

  // 문서 새로고침
  const refreshDocument = useCallback(async () => {
    await loadTasksDocument();
  }, [loadTasksDocument]);

  // 초기 로드
  useEffect(() => {
    loadTasksDocument();
  }, [loadTasksDocument]);

  // 자동 동기화 (polling)
  useEffect(() => {
    if (!autoSync || !tasksDocument) return;

    const intervalId = setInterval(() => {
      loadTasksDocument();
    }, syncInterval);

    return () => clearInterval(intervalId);
  }, [autoSync, syncInterval, tasksDocument, loadTasksDocument]);

  // 클린업
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return {
    tasksDocument,
    isLoading,
    isSyncing,
    lastSyncedAt,
    error,
    syncToDocument,
    syncFromDocument,
    createTasksDocument,
    refreshDocument,
  };
}

/**
 * 칸반보드 변경을 Tasks.md에 반영하는 헬퍼 함수
 * TaskBoard 컴포넌트에서 사용
 */
export function useTaskBoardSync(projectId: string) {
  const {
    tasksDocument,
    isLoading,
    isSyncing,
    error,
    syncToDocument,
    createTasksDocument,
  } = useTaskSync({ projectId, autoSync: false });

  // 태스크 변경 시 호출
  const onBoardChange = useCallback(
    (board: TaskBoardData) => {
      syncToDocument(board);
    },
    [syncToDocument]
  );

  // Tasks 문서 존재 확인 및 생성
  const ensureTasksDocument = useCallback(async () => {
    if (!tasksDocument && !isLoading) {
      await createTasksDocument();
    }
  }, [tasksDocument, isLoading, createTasksDocument]);

  return {
    hasTasksDocument: !!tasksDocument,
    isLoading,
    isSyncing,
    error,
    onBoardChange,
    ensureTasksDocument,
  };
}

export default useTaskSync;
